import { createBrowserRouter, Navigate } from 'react-router';
import { LocationsPage } from '../pages/LocationsPage';
import { YearOverviewPage } from '../pages/YearOverviewPage';
import { WeekOverviewPage } from '../pages/WeekOverviewPage';
import { EconomicsPage } from '../pages/EconomicsPage';
import { ConfigurationPage } from '../pages/ConfigurationPage';
import { TreatmentPlanBuilderPage } from '../pages/TreatmentPlanBuilderPage';
import { InterventionsPage } from '../pages/InterventionsPage';
import { SettingsPage } from '../pages/SettingsPage';
import { BookingsPage } from '../pages/BookingsPage';
import { ErrorPage } from '../pages/ErrorPage';
import { RootLayout } from '../pages/RootLayout';
import { AccountAdminRouteGuard } from '../components/AccountAdminRouteGuard/AccountAdminRouteGuard';
import { SettingsRedirect } from '../components/SettingsRedirect/SettingsRedirect';
import { PatientsPage } from '../pages/PatientsPage';
import { RoomsSettings } from '../components/RoomsSettings/RoomsSettings';
import { InterventionRoomsSettings } from '../components/InterventionRoomsSettings/InterventionRoomsSettings';
import { RoomBookings } from '../components/RoomBookings/RoomBookings';
import { StaffBookings } from '../components/StaffBookings/StaffBookings';
import { ClosedDayBookings } from '../components/ClosedDayBookings/ClosedDayBookings';
import { ReservedStaffTimeBookings } from '../components/ReservedStaffTimeBookings/ReservedStaffTimeBookings';
import { PatientBookings } from '../components/PatientBookings/PatientBookings';
import { RoomNeedsSettings } from '../components/RoomNeedsSettings/RoomNeedsSettings';
import { StaffCompetencesSettings } from '../components/StaffCompetencesSettings/StaffCompetencesSettings';
import { StaffSettings } from '../components/StaffSettings/StaffSettings';
import { InterventionRoomPropertiesSettings } from '../components/InterventionRoomPropertiesSettings/InterventionRoomPropertiesSettings';
import { UsersSettings } from '../components/UsersSettings/UsersSettings';
import ROUTES from './routeConstants';

const router = createBrowserRouter(
  [
    {
      path: ROUTES.root,
      element: <RootLayout />,
      children: [
        {
          index: true,
          element: <Navigate to={ROUTES.locations} replace />
        },
        {
          path: ROUTES.locations,
          element: <LocationsPage />
        },
        {
          path: ROUTES.yearOverview,
          element: <YearOverviewPage />
        },
        {
          path: ROUTES.weekOverview,
          element: <WeekOverviewPage />
        },
        {
          path: ROUTES.economics,
          element: <EconomicsPage />
        },
        // Patients is part of the daily planning work, so it lives in the main
        // navigation rather than under settings.
        {
          path: ROUTES.patients,
          element: <PatientsPage />
        },
        // Location-scoped settings — rooms, intervention rooms, employees
        {
          path: ROUTES.locationSettings,
          element: <SettingsPage />,
          children: [
            {
              index: true,
              element: <Navigate to={ROUTES.settingsRooms} replace />
            },
            {
              path: ROUTES.settingsRooms,
              element: <RoomsSettings />
            },
            {
              path: ROUTES.settingsInterventionRooms,
              element: <InterventionRoomsSettings />
            },
            {
              path: ROUTES.settingsEmployees,
              element: <StaffSettings />
            }
          ]
        },
        // Location-scoped bookings (account-admin only)
        {
          element: <AccountAdminRouteGuard />,
          children: [
            {
              path: ROUTES.locationBookings,
              element: <BookingsPage />,
              children: [
                {
                  index: true,
                  element: <Navigate to={ROUTES.bookingsRoomBookings} replace />
                },
                {
                  path: ROUTES.bookingsRoomBookings,
                  element: <RoomBookings />
                },
                {
                  path: ROUTES.bookingsStaffBookings,
                  element: <StaffBookings />
                },
                {
                  path: ROUTES.bookingsClosedDays,
                  element: <ClosedDayBookings />
                },
                {
                  path: ROUTES.bookingsReservedStaffTime,
                  element: <ReservedStaffTimeBookings />
                },
                {
                  path: ROUTES.bookingsPatientBookings,
                  element: <PatientBookings />
                }
              ]
            }
          ]
        },
        // Account-level (global) configuration
        {
          path: ROUTES.configuration,
          element: <ConfigurationPage />
        },
        {
          path: 'configuration/treatment-plans',
          element: <Navigate to={`/${ROUTES.configuration}`} replace />
        },
        {
          path: ROUTES.treatmentPlanBuilder,
          element: <TreatmentPlanBuilderPage />
        },
        {
          path: ROUTES.configurationInterventions,
          element: <InterventionsPage />
        },
        // Account-level (global) settings — same layout as location settings,
        // but only the account-admin subpages live here.
        {
          path: ROUTES.settings,
          element: <SettingsPage />,
          children: [
            {
              index: true,
              element: <SettingsRedirect />
            },
            {
              element: <AccountAdminRouteGuard />,
              children: [
                {
                  path: ROUTES.settingsRoomNeeds,
                  element: <RoomNeedsSettings />
                },
                {
                  path: ROUTES.settingsInterventionRoomProperties,
                  element: <InterventionRoomPropertiesSettings />
                },
                {
                  path: ROUTES.settingsStaffCompetences,
                  element: <StaffCompetencesSettings />
                },
                {
                  path: ROUTES.settingsUsers,
                  element: <UsersSettings />
                }
              ]
            }
          ]
        },
        {
          path: ROUTES.notFound,
          element: <ErrorPage />
        },
        {
          path: ROUTES.errorPage,
          element: <ErrorPage />
        }
      ]
    }
  ],
  { basename: '/' }
);

export default router;
